import React from "react";
import {
  Alert,
  AlertIcon,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Box,
  Collapse,
  Icon,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  TimeIcon,
  CalendarIcon,
} from "@chakra-ui/icons";
import { format, isToday, isPast } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const ReturnNotifications = ({ places, onUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const navigate = useNavigate();

  if (!places || places.length === 0) {
    return null;
  }

  // Separar por urgência
  const overduePlaces = places.filter(place =>
    isPast(new Date(place.return_contact_date)) && !isToday(new Date(place.return_contact_date))
  );
  const todayPlaces = places.filter(place =>
    isToday(new Date(place.return_contact_date))
  );
  const futurePlaces = places.filter(place =>
    !isPast(new Date(place.return_contact_date)) && !isToday(new Date(place.return_contact_date))
  );

  const getStatusColor = (place) => {
    const date = new Date(place.return_contact_date);
    if (isPast(date) && !isToday(date)) return "red";
    if (isToday(date)) return "orange";
    return "yellow";
  };

  const getStatusText = (place) => {
    const date = new Date(place.return_contact_date);
    if (isPast(date) && !isToday(date)) return "Atrasado";
    if (isToday(date)) return "Hoje";
    return "Agendado";
  };

  const handlePlaceClick = (place) => {
    navigate(`/prospeccao/${place.prospect_list_id}`);
  };

  const urgentCount = overduePlaces.length + todayPlaces.length;

  return (
    <Alert
      status={urgentCount > 0 ? "warning" : "info"}
      borderRadius="md"
      flexDirection="column"
      alignItems="start"
      py={4}
    >
      <HStack width="100%" justify="space-between" mb={2}>
        <HStack>
          <AlertIcon />
          <VStack align="start" spacing={0}>
            <Text fontWeight="bold">
              Retornos Agendados ({places.length})
            </Text>
            <Text fontSize="sm">
              {urgentCount > 0
                ? `${urgentCount} precisam de atenção urgente`
                : "Todos os retornos estão em dia"
              }
            </Text>
          </VStack>
        </HStack>

        <Button
          size="sm"
          variant="ghost"
          rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Ocultar" : "Mostrar"}
        </Button>
      </HStack>

      <Collapse in={isExpanded} animateOpacity style={{ width: "100%" }}>
        <VStack spacing={4} align="stretch" width="100%">
          {/* Atrasados */}
          {overduePlaces.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="bold" color="red.600" mb={2}>
                🚨 Atrasados ({overduePlaces.length})
              </Text>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={3}>
                {overduePlaces.map((place) => (
                  <PlaceCard key={place.id} place={place} onPlaceClick={handlePlaceClick} />
                ))}
              </Grid>
            </Box>
          )}

          {/* Hoje */}
          {todayPlaces.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="bold" color="orange.600" mb={2}>
                📅 Para Hoje ({todayPlaces.length})
              </Text>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={3}>
                {todayPlaces.map((place) => (
                  <PlaceCard key={place.id} place={place} onPlaceClick={handlePlaceClick} />
                ))}
              </Grid>
            </Box>
          )}

          {/* Futuros */}
          {futurePlaces.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="bold" color="blue.600" mb={2}>
                🗓️ Próximos ({futurePlaces.length})
              </Text>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={3}>
                {futurePlaces.slice(0, 6).map((place) => (
                  <PlaceCard key={place.id} place={place} onPlaceClick={handlePlaceClick} />
                ))}
                {futurePlaces.length > 6 && (
                  <GridItem>
                    <Box
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px"
                      borderColor="gray.200"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text color="gray.600" fontSize="sm">
                        +{futurePlaces.length - 6} outros...
                      </Text>
                    </Box>
                  </GridItem>
                )}
              </Grid>
            </Box>
          )}
        </VStack>
      </Collapse>
    </Alert>
  );
};

const PlaceCard = ({ place, onPlaceClick }) => {
  const getStatusColor = () => {
    const date = new Date(place.return_contact_date);
    if (isPast(date) && !isToday(date)) return "red";
    if (isToday(date)) return "orange";
    return "yellow";
  };

  const getStatusText = () => {
    const date = new Date(place.return_contact_date);
    if (isPast(date) && !isToday(date)) return "Atrasado";
    if (isToday(date)) return "Hoje";
    return "Agendado";
  };

  return (
    <Box
      p={3}
      bg="white"
      borderRadius="md"
      border="1px"
      borderColor="gray.200"
      cursor="pointer"
      _hover={{
        shadow: "sm",
        transform: "translateY(-1px)",
        transition: "all 0.2s",
      }}
      onClick={() => onPlaceClick(place)}
    >
      <VStack align="start" spacing={2}>
        <HStack justify="space-between" width="100%">
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
            {place.title}
          </Text>
          <Badge colorScheme={getStatusColor()} size="sm">
            {getStatusText()}
          </Badge>
        </HStack>

        <VStack align="start" spacing={1} width="100%">
          <HStack>
            <Icon as={PhoneIcon} boxSize={3} color="gray.500" />
            <Text fontSize="xs" color="gray.600">
              {place.contact_person_name}
            </Text>
          </HStack>

          <HStack>
            <Icon as={CalendarIcon} boxSize={3} color="gray.500" />
            <Text fontSize="xs" color="gray.600">
              {format(new Date(place.return_contact_date), "dd/MM/yyyy", { locale: ptBR })}
            </Text>
          </HStack>

          <HStack>
            <Icon as={TimeIcon} boxSize={3} color="gray.500" />
            <Text fontSize="xs" color="gray.600" noOfLines={1}>
              {place.prospect_list.name}
            </Text>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};

export default ReturnNotifications;